/**
 * Sends API requests with the authentication cookie, disables caching,
 * validates responses, and reports shared API errors.
 */
import "client-only";

import type { ZodType } from "zod";

type RequestConfig<T> = {
  expect: ZodType<T>;
};

type RequestConfigWithoutExpectation = {
  expect?: undefined;
};

export const AUTHENTICATION_REQUIRED_EVENT =
  "trading-platform:authentication-required";

export class PlatformApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "PlatformApiError";
  }
}

async function responseBody(response: Response): Promise<unknown> {
  const content = await response.text();
  if (!content) return undefined;

  try {
    return JSON.parse(content) as unknown;
  } catch {
    return content;
  }
}

function errorMessage(details: unknown): string {
  if (
    typeof details === "object" &&
    details !== null &&
    "error" in details &&
    typeof details.error === "string"
  ) {
    return details.error;
  }

  return "The request could not be completed.";
}

async function request<T>(
  method: string,
  path: string,
  data?: unknown,
  config?: RequestConfig<T> | RequestConfigWithoutExpectation,
): Promise<unknown> {
  const headers = new Headers();
  let body: BodyInit | undefined;

  if (data instanceof FormData) {
    body = data;
  } else if (data !== undefined) {
    headers.set("content-type", "application/json");
    body = JSON.stringify(data);
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body,
      credentials: "include",
      cache: "no-store",
    });
  } catch {
    throw new PlatformApiError("The TradingPlatform API could not be reached.");
  }

  if (!response.ok) {
    const details = await responseBody(response);
    if (response.status === 401) window.dispatchEvent(new Event(AUTHENTICATION_REQUIRED_EVENT));
    throw new PlatformApiError(errorMessage(details), response.status, details);
  }

  const payload = await responseBody(response);
  if (!config?.expect) return payload;

  const result = config.expect.safeParse(payload);
  if (!result.success) {
    console.error("TradingPlatform API response validation failed", {
      method,
      path,
      status: response.status,
      error: result.error,
    });

    throw new PlatformApiError(
      "The TradingPlatform API returned an unexpected response.",
      response.status,
      result.error.issues,
    );
  }

  return result.data;
}

function get<T>(path: string, config: RequestConfig<T>): Promise<T>;
function get(path: string, config?: RequestConfigWithoutExpectation): Promise<unknown>;
function get<T>(
  path: string,
  config?: RequestConfig<T> | RequestConfigWithoutExpectation,
): Promise<unknown> {
  return request("GET", path, undefined, config);
}

function post<T>(path: string, data: unknown, config: RequestConfig<T>): Promise<T>;
function post(
  path: string,
  data?: unknown,
  config?: RequestConfigWithoutExpectation,
): Promise<unknown>;
function post<T>(
  path: string,
  data?: unknown,
  config?: RequestConfig<T> | RequestConfigWithoutExpectation,
): Promise<unknown> {
  return request("POST", path, data, config);
}

function put<T>(path: string, data: unknown, config: RequestConfig<T>): Promise<T>;
function put(
  path: string,
  data: unknown,
  config?: RequestConfigWithoutExpectation,
): Promise<unknown>;
function put<T>(
  path: string,
  data: unknown,
  config?: RequestConfig<T> | RequestConfigWithoutExpectation,
): Promise<unknown> {
  return request("PUT", path, data, config);
}

function remove<T>(path: string, config: RequestConfig<T>): Promise<T>;
function remove(path: string, config?: RequestConfigWithoutExpectation): Promise<unknown>;
function remove<T>(
  path: string,
  config?: RequestConfig<T> | RequestConfigWithoutExpectation,
): Promise<unknown> {
  return request("DELETE", path, undefined, config);
}

export const client = { get, post, put, delete: remove };
